import type { ServiceLevel } from '../types'
import { getCarIcon } from '../assets/cars'

// Company color for cars - purple gradient
const COMPANY_COLOR = '#9362CC'

const TARIFF_INFO: Record<string, { name: string; isPremium: boolean }> = {
  econom: { name: 'Эконом', isPremium: false },
  comfort: { name: 'Комфорт', isPremium: false },
  comfortplus: { name: 'Комфорт+', isPremium: false },
  business: { name: 'Бизнес', isPremium: true },
  vip: { name: 'VIP', isPremium: true },
  premier: { name: 'Премьер', isPremium: true },
  ultimate: { name: 'Ultimate', isPremium: true },
  elite: { name: 'Elite', isPremium: true },
  child_tariff: { name: 'Детский', isPremium: false },
  lite_b2b: { name: 'Лайт', isPremium: false },
  standart_b2b: { name: 'Стандарт', isPremium: false },
  optimum_b2b: { name: 'Оптимум', isPremium: false },
  maybach: { name: 'Maybach', isPremium: true },
  minivan: { name: 'Минивэн', isPremium: false },
  premium_van: { name: 'Премиум Ван', isPremium: true },
}


interface TariffCardProps {
  tariff: ServiceLevel
  isSelected: boolean
  onSelect: () => void
  onDetails?: () => void
}

export const TariffCard = ({ tariff, isSelected, onSelect, onDetails }: TariffCardProps) => {
  const info = TARIFF_INFO[tariff.class] || { name: tariff.class, isPremium: false }
  const waitingMinutes = tariff.estimated_waiting 
    ? Math.ceil(tariff.estimated_waiting.seconds / 60) 
    : null
  
  // Get the car SVG component for this tariff class
  const CarIcon = getCarIcon(tariff.class)

  return (
    <button
      onClick={onSelect}
      onDoubleClick={onDetails}
      className={`
        flex-shrink-0 w-[100px] sm:w-[110px] p-2.5 sm:p-3 rounded-2xl transition-all duration-200 border relative overflow-hidden
        ${isSelected
          ? 'bg-white/10 border-white/30 shadow-lg'
          : 'bg-transparent border-transparent hover:bg-white/5'
        }
      `}
    >
      {/* Время ожидания бейдж */}
      {waitingMinutes && (
        <div className={`
          absolute -top-0.5 -right-0.5 px-2 py-0.5 rounded-bl-lg rounded-tr-2xl text-[10px] font-bold
          ${isSelected 
            ? info.isPremium ? 'bg-amber-500 text-white' : 'bg-primary text-white' 
            : 'bg-dark-secondary/80 text-text-secondary'
          }
        `}>
          {waitingMinutes} мин
        </div>
      )}

      {/* Машина - SVG в фирменном цвете */}
      <div className="flex items-center justify-center h-10 sm:h-12 mb-1.5 transition-transform duration-300">
        <CarIcon 
          size={70} 
          color={info.isPremium ? undefined : COMPANY_COLOR} 
        />
      </div>

      {/* Название тарифа */}
      <p className={`
        font-medium text-xs sm:text-sm truncate text-center
        ${isSelected ? 'text-white' : 'text-text-secondary'}
      `}>
        {info.name}
      </p>

      {/* Цена */}
      <p className={`
        text-sm sm:text-base font-bold text-center mt-0.5
        ${isSelected ? 'text-white' : 'text-text-primary'}
      `}>
        {tariff.price_rub} ₽
      </p>

      {/* Индикатор выбора */}
      {isSelected && (
        <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full ${info.isPremium ? 'bg-amber-400' : 'bg-white'}`} />
      )}
    </button>
  )
}

// Компактная карточка для горизонтального списка
interface CompactTariffCardProps {
  tariff: ServiceLevel
  isSelected: boolean
  onSelect: () => void
}

export const CompactTariffCard = ({ tariff, isSelected, onSelect }: CompactTariffCardProps) => {
  const info = TARIFF_INFO[tariff.class] || { name: tariff.class, isPremium: false }
  const waitingMinutes = tariff.estimated_waiting 
    ? Math.ceil(tariff.estimated_waiting.seconds / 60) 
    : null
  
  // Get the car SVG component for this tariff class
  const CarIcon = getCarIcon(tariff.class)

  return (
    <button
      onClick={onSelect}
      className={`
        flex-shrink-0 flex flex-col items-center w-[80px] sm:w-[90px] py-2 px-1.5 rounded-xl transition-all duration-200 snap-start
        ${isSelected
          ? 'bg-white/10'
          : 'hover:bg-white/5 active:scale-95'
        }
      `}
    >
      {/* Машина с бейджем времени - SVG в фирменном цвете */}
      <div className="relative mb-1.5 w-full flex justify-center">
        <div className={`${isSelected ? 'drop-shadow-md' : 'opacity-90'}`}>
          <CarIcon 
            size={64} 
            color={info.isPremium ? undefined : COMPANY_COLOR} 
          />
        </div>
        {waitingMinutes && (
          <div className={`
            absolute -top-1 right-0 px-1.5 py-0.5 rounded text-[9px] font-bold
            ${isSelected 
              ? info.isPremium ? 'bg-amber-500 text-white' : 'bg-primary text-white' 
              : 'bg-dark-secondary/80 text-text-muted'
            }
          `}>
            {waitingMinutes} мин
          </div>
        )}
      </div>

      {/* Название */}
      <p className={`
        text-[11px] sm:text-xs font-medium truncate w-full text-center
        ${isSelected ? 'text-white' : 'text-text-secondary'}
      `}>
        {info.name}
      </p>

      {/* Цена */}
      <p className={`
        text-sm sm:text-base font-bold
        ${isSelected ? 'text-white' : 'text-text-primary'}
      `}>
        {tariff.price_rub} ₽
      </p>

      {/* Подчёркивание выбранного */}
      {isSelected && (
        <div className={`w-6 h-0.5 rounded-full mt-1 ${info.isPremium ? 'bg-amber-400' : 'bg-white'}`} />
      )}
    </button>
  )
}

export default TariffCard
